import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Buttons } from "../../atoms/Buttons";
import { ControlText } from "../../atoms/Controls/ControlText";
import { Modals } from "../../molecules/Modals";
import {
  ModalFormBox,
  ModalFormDiv,
  ModalFormSection,
} from "../../molecules/Modals/Modals.style";
import { useRecoilState } from "recoil";
import { userState } from "../../../states/loginUser";
import { useForm } from "react-hook-form";
import { Nullable } from "../../../common/types/common";
import { CreateMemberInputs } from "./MemberCreateModal";
import { yupResolver } from "@hookform/resolvers/yup";
import { modalMemberSchema } from "../../molecules/Modals/ModalMember/modal-member-yup";
import { request } from "../../../common/api";
import { BaseResponse } from "../../../common/types/baseRespones";
import { GET_USER } from "../../../common/querykeys";
import { stmUser } from "../../../common/types/stmUser";

interface ChangeUserInfoModalProps {
  formId: string;
  setModalShow: any;
}

export const ChangeUserInfo = ({
  formId,
  setModalShow,
}: ChangeUserInfoModalProps) => {
  const queryClient = useQueryClient();
  const [userInfo, setUserInfo] = useRecoilState(userState);

  const methods = useForm<Nullable<CreateMemberInputs>>({
    defaultValues: { ...userInfo, password: undefined },
    resolver: yupResolver(modalMemberSchema),
  });
  const { control, handleSubmit } = methods;

  const { mutateAsync: updateStmUser } = useMutation({
    mutationFn: (inputs: any) => {
      return request<BaseResponse>({
        method: "PUT",
        url: `/stm-user/${userInfo?.oid}`,
        data: inputs,
      });
    },
    onSuccess: (res: BaseResponse) => {
      queryClient.invalidateQueries({ queryKey: [GET_USER] });
      if (res.success) {
        setModalShow(false);
      }
    },
  });

  // ❇️ 로그인 한 user 정보 가져오기
  const { mutateAsync: getUserId } = useMutation({
    mutationFn: () => {
      return request<BaseResponse<{ user: stmUser }>>({
        method: "GET",
        url: `/stm-user/user/${userInfo?.userId}`,
      });
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: [GET_USER] });

      if (data.success && data.data?.user) {
        // ❇️ data.data.user가 존재할 때만 실행
        setUserInfo(data.data.user);
      } else {
        console.error("User data not found or request failed", data);
      }
    },
  });

  /* ✔️ 직책 selectedOption 불러오기 */
  // const { data: stmUserDutiesOptions } = useQuery<SelectOption[]>({
  //   queryKey: [GET_PROJECT_PM],
  //   queryFn: () => {
  //     return request<SelectOption[]>({
  //       method: "GET",
  //       url: `/stm-code/ST_USER`,
  //     });
  //   },
  //   // 받아온 Options 데이터 정렬
  //   select: (data) =>
  //     data.sort((a, b) => {
  //       const order = [
  //         "DUTIES_CEO", // 대표
  //         "DUTIES_EXCUTIVE_MANAGER", // 부장
  //         "DUTIES_SENIOR_MANAGER", // 차장
  //         "DUTIES_MANAGER", // 과장
  //         "DUTIES_ASSISTANT_MANAGER", // 대리
  //         "DUTIES_SENIOR_STAFF", // 주임
  //         "DUTIES_STAFF", // 사원
  //       ];
  //       return order.indexOf(a.value ?? "") - order.indexOf(b.value ?? "");
  //     }),
  // });
  /* ✔️ 부서 selectedOption 불러오기 */
  // const { data: stmUserDepartmentOptions } = useQuery<SelectOption[]>({
  //   queryKey: ["GET_USER_DEPARTMENT"],
  //   queryFn: () => {
  //     return request<SelectOption[]>({
  //       method: "GET",
  //       url: `/stm-code/ST_DEPARTMENT`,
  //     });
  //   },
  // });

  return (
    <Modals
      modalTitle={"사용자 정보 변경"}
      formId={formId}
      setModalShow={setModalShow}
      modalButtons={
        <>
          <Buttons
            type="button"
            size="md"
            layout="primary"
            label="확인"
            form={formId}
            onClick={handleSubmit(
              async (data: Nullable<CreateMemberInputs>) => {
                await updateStmUser(data);

                /* ❇️ 현재 로그인한 사용자의 userId와 변경 대상 userId가 같을때 상태 업데이트 */
                if (userInfo?.userId) {
                  await getUserId();
                }
              }
            )}
          />
          <Buttons
            type="button"
            size="md"
            layout="secondary"
            label="취소"
            onClick={() => setModalShow(false)}
          />
        </>
      }
    >
      <ModalFormBox id={formId} $flexDirection={"row"}>
        <ModalFormSection>
          <ModalFormDiv>
            <ControlText
              control={control}
              size="md"
              width={200}
              direction="column"
              label="사용자 ID"
              placeholder="사용자 ID를 입력해주세요."
              name="userId"
              disabled
            />
            <ControlText
              control={control}
              size="md"
              width={200}
              direction="column"
              label="사용자명"
              placeholder="사용자명을 입력해주세요."
              name="userName"
            />
            <ControlText
              type="date"
              control={control}
              size="md"
              width={200}
              direction="column"
              label="입사일"
              placeholder="입사일을 입력해주세요."
              name="joinDate"
            />
          </ModalFormDiv>
          <ModalFormDiv>
            <ControlText
              control={control}
              size="md"
              width={200}
              direction="column"
              label="전화번호"
              placeholder="전화번호를 입력해주세요."
              name="tel"
            />
            <ControlText
              control={control}
              size="md"
              width={200}
              direction="column"
              label="휴대전화"
              placeholder="휴대전화를 입력해주세요."
              name="hp"
            />
            <ControlText
              control={control}
              size="md"
              width={200}
              direction="column"
              label="이메일"
              placeholder="이메일을 입력해주세요."
              name="email"
            />
          </ModalFormDiv>
        </ModalFormSection>
      </ModalFormBox>
    </Modals>
  );
};
